import React, { useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import { Button } from "@components/ui/button";
import { getUserDataLists } from "@services/api/amlAnomalyDetection";
import PageLoader from "@components/loaders/PageLoader";
import {
  AnomalyData,
  CustomSelectDlistProps,
} from "@components/common/CommonTypes";
import CustomSelect from "@components/common/CustomSelect";
import { toast } from "@components/ui/use-toast";
import { addRow } from "@services/api/aiEngineProcessing";

interface Props {
  show: boolean;
  onClose: () => void;
  data: any;
}

const AddToDataList: React.FC<Props> = ({ show, onClose, data }) => {
  const [allEvents, setAllEvents] = React.useState<AnomalyData[]>([]);
  const [selectedEvent, setSelectedEvent] = React.useState<string | null>(null);
  const [selectedField, setSelectedField] = React.useState<string | null>(null);
  const [currentField, setCurrentField] = React.useState<string | null>(null);
  const [selectedDatalist, setSelectedDatalist] = React.useState<string | null>(
    null,
  );
  const [dataLists, setDataLists] = React.useState<CustomSelectDlistProps[]>(
    [],
  );
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [columns, setColumns] = React.useState<string[]>([]);
  const [selectedColumn, setSelectedColumn] = React.useState<string | null>(
    null,
  );
  const [fieldType, setFieldType] = React.useState<string | null>(null);

  // Fields to select in the dropdown
  const fieldsToSelect = [
    {
      field: "SourceIp",
      name: "Source IP",
      type: "ip",
    },
    {
      field: "DestinationIp",
      name: "Destination IP",
      type: "ip",
    },
    {
      field: "AnomalousIpMac",
      name: "Anomalous IP - MAC",
      type: "mac",
    },
  ];

  // Effect to set all events when data changes
  useEffect(() => {
    if (data?.eventData) {
      const parsedEvents: any = [];
      data.eventData.forEach((event: any) => {
        let parsedEventData = {};
        try {
          parsedEventData = JSON.parse(event.EventData);
        } catch (e) {
          console.error("Failed to parse EventData:", e);
        }
        const exists = parsedEvents.find(
          (e: any) => e.globalID === event.globalID,
        );
        if (!exists) {
          parsedEvents.push({
            ...event,
            ParsedEventData: parsedEventData,
          });
        }
      });

      setAllEvents(parsedEvents);
    }
  }, [data]);

  // fetch user data lists
  const fetchDataLists = async () => {
    try {
      setIsLoading(true);
      const response = await getUserDataLists();
      if (response?.msg === "success") {
        const temp = response.data.map((item: any) => ({
          value: item.list_name,
          label: item.list_name,
          parameters: {
            columns: (item.parameters?.columns || []).map((col: any) => ({
              column_name: col.column_name,
              column_type: col.column_type,
            })),
          },
        }));

        setDataLists(temp);
      }
      setIsLoading(false);
    } catch (error) {
      console.error("Failed to fetch user data lists:", error);
    }
  };

  // Fetch data lists on component mount
  useEffect(() => {
    fetchDataLists();
  }, []);

  // Function to select the field
  const handleSelectField = (field: string) => {
    setSelectedField(field);

    const selectedFieldInfo = fieldsToSelect.find((f) => f.field == field);
    const selectedType = selectedFieldInfo?.type || null;
    setFieldType(selectedType);

    const eventObj: any = allEvents.find(
      (event) => event.EventName == selectedEvent,
    );

    if (!eventObj) {
      setCurrentField("N/A");
      return;
    }

    if (eventObj[field]) {
      setCurrentField(eventObj[field]);
    } else if (eventObj.ParsedEventData?.[field]) {
      setCurrentField(eventObj.ParsedEventData[field]);
    } else {
      setCurrentField("N/A");
    }
  };

  // Function to select a datalist and its columns
  const handleSelectDatalist = (value: string) => {
    setSelectedDatalist(value);

    const matchedList = dataLists.find((list) => list.value == value);

    if (
      matchedList &&
      matchedList.parameters &&
      matchedList.parameters.columns &&
      matchedList.parameters.columns.length > 0
    ) {
      const cols = matchedList.parameters.columns
        .filter((col: any) => {
          if (!fieldType) return false;
          return col.column_type?.toLowerCase() == fieldType.toLowerCase();
        })
        .map((col: any) => col.column_name);

      if (cols.length === 0) {
        toast({
          title: "No matching columns",
          description: `No columns in the selected datalist match the selected field type (${fieldType}).`,
          variant: "destructive",
        });
        setColumns([]);
        setSelectedColumn(null);
        setSelectedDatalist(null);
      } else {
        setColumns(cols);
      }
    } else {
      toast({
        title: "No columns found in datalist",
        description: "Selected datalist has no defined columns.",
        variant: "destructive",
      });
      setColumns([]);
      setSelectedColumn(null);
      setSelectedDatalist(null);
    }
  };

  // Function to add the selected field value into the selected datalist column
  const addToDatalist = async () => {
    if (
      !selectedEvent ||
      !selectedField ||
      !selectedDatalist ||
      !selectedColumn ||
      !currentField
    ) {
      toast({
        title: "Missing fields !",
        description:
          "Please select event, field, datalist, and column before submitting.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsLoading(true);

      const payload = {
        data: [
          {
            colName: selectedColumn,
            colVal: currentField,
          },
        ],
        dlName: selectedDatalist,
      };

      const result = await addRow(payload);

      if (result?.status === "success") {
        toast({
          title: "Field added to Datalist",
          description: `${currentField} has been successfully added to the ${selectedDatalist}`,
          variant: "success",
        });
        onClose();
      } else {
        toast({
          title: "Error!",
          description: `Failed to add ${currentField} into the ${selectedDatalist}`,
          variant: "destructive",
        });
        onClose();
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to add to datalist.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Effect to reset state when dialog is closed
  useEffect(() => {
    return () => {
      setAllEvents([]);
      setSelectedEvent(null);
      setSelectedField(null);
      setCurrentField(null);
      setSelectedDatalist(null);
      setDataLists([]);
      setColumns([]);
      setSelectedColumn(null);
      setFieldType(null);
    };
  }, [show]);

  return (
    <>
      <PageLoader show={isLoading} />
      <Dialog open={show} onOpenChange={onClose} modal={false}>
        <DialogContent
          onInteractOutside={(e) => {
            e.preventDefault();
          }}
        >
          <DialogHeader>
            <DialogTitle>Add To Datalist</DialogTitle>
          </DialogHeader>

          <div className="w-full relative border-t">
            <Select
              value={selectedEvent ?? ""}
              onValueChange={(value) => setSelectedEvent(value)}
            >
              <SelectTrigger className="w-full mt-5">
                <SelectValue placeholder="Select Event" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {allEvents.map((event) => (
                    <SelectItem
                      key={event.globalID}
                      value={event.EventName ?? ""}
                    >
                      {event.EventName}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>

            {selectedEvent && (
              <Select onValueChange={(value) => handleSelectField(value)}>
                <SelectTrigger className="w-full mt-5">
                  <SelectValue placeholder="Select field to add" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {fieldsToSelect.map((elem) => {
                      const eventObj: any = allEvents.find(
                        (event) => event.EventName == selectedEvent,
                      );
                      let fieldValue = "N/A";
                      if (eventObj) {
                        fieldValue =
                          eventObj[elem.field] ??
                          eventObj.ParsedEventData?.[elem.field] ??
                          "N/A";
                      }
                      return (
                        <SelectItem key={elem.field} value={elem.field}>
                          {`${elem.name} - ${fieldValue}`}
                        </SelectItem>
                      );
                    })}
                  </SelectGroup>
                </SelectContent>
              </Select>
            )}

            {selectedField != null && (
              <div className="w-full mt-5">
                <CustomSelect
                  data={dataLists.filter((list: any) =>
                    list.parameters.columns.some((col: any) => {
                      if (fieldType == "ip") {
                        return col.column_type.toLowerCase().includes("ip");
                      } else if (fieldType == "mac") {
                        return col.column_type.toLowerCase().includes("mac");
                      } else {
                        return true;
                      }
                    }),
                  )}
                  placeholder="Select datalist to add"
                  onSelect={(value) => handleSelectDatalist(value)}
                />
              </div>
            )}

            {columns.length > 0 && (
              <Select
                onValueChange={(value) => setSelectedColumn(value)}
                value={selectedColumn ?? ""}
              >
                <SelectTrigger className="w-full mt-5">
                  <SelectValue placeholder="Select column to insert into" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {columns.map((col) => (
                      <SelectItem key={col} value={col}>
                        {col}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            )}

            <Button
              type="submit"
              className="mt-5 float-right"
              onClick={addToDatalist}
            >
              Submit
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AddToDataList;
